// ============================================
// MOBILE LEAD CARDS — shown below the md breakpoint instead of the table
// src/components/MobileLeadList.jsx
// What a caller needs on one card: who, what they want, why now, what happened
// last time, where they came from. Actions:
//   Call    → dials; coming back to Heroshhi asks "How did it go?" (onCalled)
//   Message → Heroshhi's own send window (business number, saved in the chat)
//   ›       → opens the lead
// The pager and "select all" live in the Leads tab itself (shared with computers).
// ============================================
import React from 'react';
import { formatPhone } from '../utils/phone';
import { Phone, MessageCircle, ChevronRight } from './icons/HeroshhiIcons';
import { whyNow, lastOutcome, interestOf, tierOf, sourceCampaign } from './leadInsights';

export default function MobileLeadList({ leads, onOpen, onMessage, onCalled, selectedIds = [], onToggleSelect }) {
  if (!leads || leads.length === 0) return null;
  return (
    <div className="space-y-2.5 md:hidden">
      {leads.map((lead) => {
        const why = whyNow(lead);
        const last = lastOutcome(lead);
        const interest = interestOf(lead);
        const tier = tierOf(lead);
        const sc = sourceCampaign(lead);
        const digits = String(lead.phone || '').replace(/\D/g, '');
        const tel = digits ? `tel:+${digits.length === 10 ? '91' + digits : digits}` : null;
        const selected = selectedIds.includes(lead._id);
        return (
          <div key={lead._id} className={`bg-white rounded-xl border p-3.5 ${selected ? 'border-[#10203A] bg-slate-50' : 'border-gray-200'}`}>
            <div className="flex items-start gap-3">
              {onToggleSelect && (
                <input type="checkbox" checked={selected} onChange={() => onToggleSelect(lead._id)}
                  aria-label={`Select ${lead.name || lead.phone}`} className="mt-1 w-5 h-5 flex-none rounded accent-[#10203A]" />
              )}
              <button type="button" onClick={() => onOpen(lead)} className="flex-1 min-w-0 text-left">
                <span className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-base font-bold text-[#10203A] break-words">{lead.name || formatPhone(lead.phone) || 'Unnamed'}</span>
                  {lead.segment === 'hot' && <span className="text-xs font-extrabold text-orange-800">Hot</span>}
                  {lead.segment === 'warm' && <span className="text-xs font-extrabold text-amber-800">Warm</span>}
                  {tier && <span className="text-[11px] font-bold px-1.5 py-px rounded-md bg-slate-100 text-[#10203A]">{tier}</span>}
                </span>
                {interest && <span className="block text-sm text-gray-600 truncate">{interest}</span>}
                {why.text && <span className={`block text-sm font-bold mt-1 ${why.warn ? 'text-orange-800' : 'text-[#10203A]'}`}>{why.text}</span>}
                <span className="block text-sm text-[#10203A] mt-0.5 line-clamp-2">{last.text}{last.by ? <span className="text-gray-600"> · {last.by}</span> : null}</span>
                {(sc.source || sc.detail) && <span className="block text-xs text-gray-600 mt-0.5 truncate">{[sc.source, sc.detail].filter(Boolean).join(' · ')}</span>}
              </button>
            </div>
            <div className="grid grid-cols-[1fr_1fr_44px] gap-2 mt-3">
              {tel ? (
                <a href={tel} onClick={() => onCalled && onCalled(lead)}
                  className="min-h-[44px] rounded-xl bg-[#F2B12A] text-[#10203A] text-sm font-extrabold flex items-center justify-center gap-1.5"><Phone size={16} /> Call</a>
              ) : <span className="min-h-[44px] rounded-xl bg-gray-100 text-gray-500 text-sm font-bold flex items-center justify-center">No number</span>}
              <button type="button" onClick={() => onMessage && onMessage(lead)}
                className="min-h-[44px] rounded-xl border border-gray-300 text-[#10203A] text-sm font-bold flex items-center justify-center gap-1.5"><MessageCircle size={16} /> Message</button>
              <button type="button" onClick={() => onOpen(lead)} aria-label={`Open ${lead.name || 'lead'}`}
                className="min-h-[44px] rounded-xl border border-gray-300 text-[#10203A] flex items-center justify-center"><ChevronRight size={18} /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}